import React, { useMemo, useState } from 'react';
import { ChevronDown, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HistoryThread, ageLabel, groupThreads, matchThreads } from '../askai-history';

export interface ConversationMenuProps {
    threads: HistoryThread[];
    /** The conversation on screen, so the title says which one it is. */
    activeUuid: string | null;
    onOpen: (uuid: string) => void;
    onNew: () => void;
    /** Delete one conversation. Absent while the panel has no way to. */
    onRemove?: (uuid: string) => Promise<void>;
}

/**
 * The panel's title, and the way back to an earlier conversation.
 *
 * The title used to read "New conversation" whatever was on screen, and the only
 * route to the history was the rail that appears when the panel is expanded — so
 * in the docked panel, which is where most of this is used, past conversations
 * were unreachable. Clicking the title opens them.
 */
export function ConversationMenu({ threads, activeUuid, onOpen, onNew, onRemove }: ConversationMenuProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    /** Which row has been asked about, once the bin has been pressed. */
    const [pendingDelete, setPendingDelete] = useState<HistoryThread | null>(null);
    const [deleting, setDeleting] = useState(false);

    const active = threads.find((th) => th.uuid === activeUuid);
    const groups = useMemo(() => groupThreads(matchThreads(threads, query)), [threads, query]);

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        // Cleared on the way out: reopening onto a previous search is a history
        // that looks like it has lost most of itself. The same goes for a
        // half-answered question about deleting something.
        if (!next) {
            setQuery('');
            setPendingDelete(null);
        }
    };

    const titleOf = (thread: HistoryThread) =>
        thread.title?.trim() || (t('ASSISTANT.HISTORY.UNTITLED', 'Untitled') as string);

    const label = active?.title?.trim()
        || (t('ASSISTANT.NEW_CONVERSATION', 'New conversation') as string);

    /**
     * The confirmation, in the row itself.
     *
     * It was a dialog in the middle of the window, which meant pressing a bin
     * on the right of a narrow list sent the cursor across the screen and back
     * to decide about a single row. Here the answer is where the question was
     * asked, and the row it concerns is the row you are looking at — so it does
     * not need to name the conversation the way a centred dialog did.
     */
    const confirmRow = (thread: HistoryThread) => (
        <div className="flex w-full items-center gap-1 py-1.5 ps-4 pe-2">
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {t('ASSISTANT.HISTORY.DELETE_ASK', 'Delete this conversation?')}
            </span>

            <button
                type="button"
                disabled={deleting}
                onClick={() => setPendingDelete(null)}
                className="shrink-0 rounded-md px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
                {t('COMMON.CANCEL', 'Cancel')}
            </button>

            <button
                type="button"
                disabled={deleting}
                aria-label={
                    t('ASSISTANT.HISTORY.DELETE_CONFIRM_ONE', 'Yes, delete "{{title}}"', {
                        title: titleOf(thread),
                    }) as string
                }
                onClick={async () => {
                    if (!onRemove) return;
                    setDeleting(true);
                    try {
                        await onRemove(thread.uuid);
                        setPendingDelete(null);
                    } finally {
                        // Whatever happened, the row stops waiting. A failure
                        // is reported in the panel, not by a row that spins
                        // for ever.
                        setDeleting(false);
                    }
                }}
                className="flex shrink-0 items-center gap-1 rounded-md border border-destructive/40 px-2 py-1 text-[11px] font-medium text-destructive transition-colors hover:bg-destructive/5 disabled:opacity-60"
            >
                {deleting && <Loader2 className="size-3 animate-spin" />}
                {t('COMMON.DELETE', 'Delete')}
            </button>
        </div>
    );

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={open}
                    data-testid="askai-title"
                    className="-ms-1.5 flex min-w-0 items-center gap-1 px-1.5 py-0.5 transition-colors hover:bg-accent"
                >
                    {/* A long title truncates rather than pushing the window
                        controls off the edge of the panel. */}
                    <span className="truncate text-sm font-medium">{label}</span>
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                </button>
            </PopoverTrigger>

            <PopoverContent align="start" sideOffset={4} className="w-80 max-w-[calc(100vw-32px)] p-0">
                <div className="border-b p-2">
                    <div className="flex items-center gap-2 rounded-lg border px-2 py-1">
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('ASSISTANT.HISTORY.SEARCH', 'Search') as string}
                            className="min-w-0 flex-1 border-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                        />
                        <Search className="size-4 shrink-0 text-muted-foreground" />
                    </div>
                </div>

                <div className="max-h-80 overflow-y-auto py-1">
                    {groups.length === 0 && (
                        <p className="px-4 py-4 text-center text-xs text-muted-foreground">
                            {query
                                ? t('ASSISTANT.HISTORY.NO_MATCH', 'Nothing matches that.')
                                : t('ASSISTANT.HISTORY.EMPTY', 'No conversations yet.')}
                        </p>
                    )}

                    {groups.map((group) => (
                        <div key={group.key}>
                            <p className="px-4 pt-2.5 pb-1 text-[11px] font-medium text-muted-foreground">
                                {t(group.key, group.fallback)}
                            </p>

                            {group.threads.map((thread) => (
                                /*
                                 * A row, not a button.
                                 *
                                 * Deleting needs its own control, and a button
                                 * inside a button is neither valid nor
                                 * clickable. The row is a div; opening and
                                 * deleting are two siblings inside it.
                                 */
                                <div
                                    key={thread.uuid}
                                    className={cn(
                                        'group flex w-full items-center transition-colors',
                                        // No hover tint while it is asking: the
                                        // row is a question now, not a target.
                                        pendingDelete?.uuid !== thread.uuid && 'hover:bg-accent',
                                        thread.uuid === activeUuid && 'bg-accent',
                                    )}
                                >
                                    {pendingDelete?.uuid === thread.uuid ? (
                                        confirmRow(thread)
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => { onOpen(thread.uuid); handleOpenChange(false); }}
                                                className="flex min-w-0 flex-1 items-center gap-3 py-2 ps-4 pe-2 text-start"
                                            >
                                                <span className="min-w-0 flex-1 truncate text-xs">
                                                    {titleOf(thread)}
                                                </span>
                                                <span className="shrink-0 text-[11px] text-muted-foreground">
                                                    {ageLabel(thread.lastMessageAt)}
                                                </span>
                                            </button>

                                            {/*
                                              * Kept out of the way until wanted,
                                              * and reachable by keyboard whether
                                              * or not a pointer hovers the row.
                                              */}
                                            {onRemove && (
                                                <button
                                                    type="button"
                                                    /* Named, not just "Delete
                                                       conversation": a list of
                                                       identical labels tells a
                                                       screen-reader user nothing
                                                       about which one they are
                                                       about to lose. */
                                                    aria-label={
                                                        t('ASSISTANT.HISTORY.DELETE_ONE', 'Delete "{{title}}"', {
                                                            title: titleOf(thread),
                                                        }) as string
                                                    }
                                                    onClick={() => setPendingDelete(thread)}
                                                    className="me-2 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100"
                                                >
                                                    <Trash2 className="size-3.5" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="border-t p-2">
                    <button
                        type="button"
                        onClick={() => { onNew(); handleOpenChange(false); }}
                        className="flex w-full items-center justify-center gap-1.5 rounded-lg border bg-background px-4 py-2 text-xs font-medium transition-colors hover:bg-accent"
                    >
                        <Plus className="size-4" />
                        {t('ASSISTANT.NEW_CONVERSATION', 'New conversation')}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
